<script lang="ts" setup>
const { $api } = useNuxtApp()
const route = useRoute()
const { t } = useI18n()
const { rupiahFormat } = useCurrency()
const router = useRouter()

const isLoadingPage = ref(true)

const categories = ref<any>([])
const jobTypes = ref<any>([])
const jlptLevel = ref<any>([])

const salaryRangeOptions = ref({
    maximum_salary: 0,
    minimum_salary: 0
})

const salaryRange = ref<any>([])
const salaryRangeText = ref<any>(null)

const isClearFilter = defineModel({ type: Boolean })

const jftStatusOptions = [
    {
        title: t('Not required'),
        value: false
    },
    { title: t('Need to pass'), value: true }
]

const datePostedOptions = [
    { title: t('Last 3 Days'), value: 'last_3_days' },
    { title: t('Last 7 Days'), value: 'last_7_days' },
    { title: t('Last 30 Days'), value: 'last_30_days' }
]
export interface JobFilter {
    category_id?: number;
    job_type_id?: number;
    jlpt_level?: string ;
    is_jft_required?: boolean;
    minimum_salary?: number;
    maximum_salary?: number;
    date_posted?: string;
}
const filter =ref<JobFilter>({})

let debounceTimer: any = null
const debouncedEmit = (val: JobFilter) => {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
        emit('set-filter', val)
    }, 500) // Adjust the delay (500ms) as needed
}

const setSalaryRange = (isClear: boolean = false) => {
    if (route.query.minimum_salary || route.query.maximum_salary) {
        if (isClear) {
            salaryRange.value = []
            salaryRangeText.value = null
        } else {
            salaryRange.value = [
                Number(route.query.minimum_salary) || 0,
                Number(route.query.maximum_salary) || 0
            ]
            salaryRangeText.value = `Rp${salaryRange.value[0].toLocaleString()} - Rp${salaryRange.value[1].toLocaleString()}`
        }
    } else {
        salaryRange.value = []
        salaryRangeText.value = null
    }
}

const setFilter = () => {
    filter.value = {
        category_id: Number(route.query.category_id) || undefined,
        job_type_id: Number(route.query.job_type_id) || undefined,
        jlpt_level: route.query.jlpt_level || undefined,
        is_jft_required:
            route.query.is_jft_required === 'true'
                ? true
                : route.query.is_jft_required === 'false'
                ? false
                : undefined,
        minimum_salary: Number(route.query.minimum_salary) || undefined,
        maximum_salary: Number(route.query.maximum_salary) || undefined,
        date_posted: route.query.date_posted || undefined
    }

    setSalaryRange()
}

const emit = defineEmits(['set-filter'])

const resetSalaryFilter = () => {
    // 1. Reset local state
    salaryRangeText.value = null
    salaryRange.value = []

    // 2. Clear from the filter object
    filter.value.minimum_salary = undefined
    filter.value.maximum_salary = undefined

    // 3. Update the URL (Removing only salary params, keeping others like job_type_id)
    const query = { ...route.query }
    delete query.minimum_salary
    delete query.maximum_salary

    router.push({ query })
}

onMounted(() => {
    nextTick(async () => {

        setFilter()

        isLoadingPage.value = true
        const { data: categoriesData } = await $api.master.getJobInterests()

        categories.value = categoriesData.value.data

        const { data: jobTypeData } = await $api.master.getJobType()
        jobTypes.value = jobTypeData.value.data

        const { data: jlptLevelData } = await $api.master.getJlptLevel()
        jlptLevel.value = [
            ...jlptLevelData.value.data,
            { name: t('Not Required'), value: 'none' }
        ]

        const { data: salaryRangeData } = await $api.master.getSalaryRange()
        salaryRangeOptions.value = salaryRangeData.value.data

        // if (!route.query.minimum_salary && !route.query.maximum_salary) {
        //     salaryRange.value = [
        //         undefined, // This will be 0
        //         undefined
        //     ]
        // }

        isLoadingPage.value = false
    })
})
watch(
    filter,
    (val) => {
        if (!isClearFilter.value) {
            debouncedEmit(val)
        } else {
            isClearFilter.value = false
        }
    },
    { deep: true }
)

watch(isClearFilter, (val) => {
    if (val) {
        filter.value = {
            category_id: undefined,
            job_type_id: undefined,
            jlpt_level: undefined,
            is_jft_required: undefined,
            minimum_salary: undefined,
            maximum_salary: undefined,
            date_posted: undefined
        }
        salaryRange.value = []

        salaryRangeText.value = null
        setSalaryRange(true)
    }
})


const updateSalaryFilter = useDebounceFn(() => {
    if (salaryRange.value && salaryRange.value.length === 2) {
        filter.value.minimum_salary = salaryRange.value[0]
        filter.value.maximum_salary = salaryRange.value[1]
    }
}, 500)

watch(
  salaryRange,
  (newVal) => {
    if (newVal && newVal.length === 2) {
      // 1. Update the display text
      salaryRangeText.value = `Rp${newVal[0].toLocaleString()} - Rp${newVal[1].toLocaleString()}`
      
      // 2. Update the actual filter values so the API watcher triggers
      filter.value.minimum_salary = newVal[0]
      filter.value.maximum_salary = newVal[1]
    }
  },
  { deep: true }
)


watch(salaryRangeText, (val) => {
  if (!val) {
    // Reset filter
    filter.value.minimum_salary = undefined
    filter.value.maximum_salary = undefined
    // Reset slider to empty
    salaryRange.value = []
  }
})

import { useDebounceFn } from '@vueuse/core'

const openSalaryFilter = ref(false)
</script>

<template>
    <VRow>
        <VCol v-if="isLoadingPage" cols="12">
            <VSkeletonLoader height="40"></VSkeletonLoader>
        </VCol>
        <template v-else>
            <VCol cols="12" md="4" lg="2">
                <VSelect
                    v-model="filter.job_type_id"
                    :placeholder="t('Job Type')"
                    :label="t('Job Type')"
                    :items="jobTypes"
                    item-value="id"
                    item-title="name"
                    bg-color="surface"
                    density="compact"
                    clearable
                ></VSelect>
            </VCol>
            <VCol cols="12" md="4" lg="2">
                <VSelect
                    v-model="filter.jlpt_level"
                    :placeholder="t('JLPT Level')"
                    :items="jlptLevel"
                    :label="t('JLPT Level')"
                    item-title="name"
                    bg-color="surface"
                    density="compact"
                    clearable
                ></VSelect>
            </VCol>
            <VCol cols="12" md="4" lg="2">
                <VSelect
                    v-model="filter.is_jft_required"
                    :placeholder="t('JFT Status')"
                    :label="t('JFT Status')"
                    :items="jftStatusOptions"
                    bg-color="surface"
                    density="compact"
                    clearable
                >
                </VSelect>
            </VCol>
            <VCol cols="12" md="4" lg="2">
                <VSelect
                    v-model="filter.date_posted"
                    :placeholder="t('Date Posted')"
                    :label="t('Date Posted')"
                    :items="datePostedOptions"
                    bg-color="surface"
                    density="compact"
                    clearable
                >
                </VSelect>
            </VCol>
            <VCol cols="12" md="4" lg="2">
                <VMenu
                    v-model="openSalaryFilter"
                    location="bottom center"
                    :close-on-content-click="false"
                >
                    <template #activator="{ props }">
                        <VSelect
                            :label="t('Salary')"
                            v-bind="props"
                            v-model="salaryRangeText"
                            readonly=""
                            density="compact"
                            bg-color="surface"
                            :placeholder="t('Salary')"
                            
                        >
                        </VSelect>
                    </template>

                    <VCard width="360" class="mt-2">
                        <VCardText>
                            <div class="d-flex justify-space-between">
                                <span>
                                    {{
                                        salaryRange[0]
                                            ? rupiahFormat(salaryRange[0])
                                            : 'Rp -'
                                    }}</span
                                >
                                <span>
                                    {{
                                        salaryRange[1]
                                            ? rupiahFormat(salaryRange[1])
                                            : 'Rp -'
                                    }}</span
                                >
                            </div>
                            <VRangeSlider
                                v-model="salaryRange"
                                :max="salaryRangeOptions.maximum_salary"
                                :min="salaryRangeOptions.minimum_salary"
                                :step="500000"
                                :thumb-label="false"
                            >
                                <template v-slot:thumb-label="{ modelValue }">
                                    {{ rupiahFormat(modelValue) }}
                                </template></VRangeSlider
                            >
                        </VCardText>
                    </VCard>
                </VMenu>
            </VCol>
            <VCol cols="12" md="4" lg="2">
                <VSelect
                    :placeholder="t('Job Category')"
                    v-model="filter.category_id"
                    :label="t('Job Category')"
                    :items="categories"
                    item-value="id"
                    item-title="name"
                    bg-color="surface"
                    density="compact"
                    clearable
                ></VSelect>
            </VCol>
        </template>
    </VRow>
</template>
